import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFutbol } from "@fortawesome/pro-regular-svg-icons";
import { faCircleArrowDown } from "@fortawesome/pro-solid-svg-icons/faCircleArrowDown";

import {
  CardEvent,
  CardTypeEnum,
  Image,
  JerseyModeEnum,
  LineupPosition,
  Player,
  PlayerListElement,
  TeamListElement
} from "../../../../../client/api";
import PlayerImage from "../../../PlayerImage";
import StoreMiniBanner from "../../../StoreMiniBanner";
import ScaleFade from "../../../Animated/ScaleFade";
import { ClavaContext } from "../../../../../config/contexts";
import { translate } from "../../../../../config/translator";
import { pathPlayerView } from "../../../../../config/routes";
import IconWithBackground from "../../../IconWithBackground";
import PlayerRating from "../../../PlayerRating";

const PlayerChip: React.FC<{
  image?: Image;
  givenName: string;
  familyName: string;
  onClick: () => void;
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  children?: React.ReactNode;
  jerseyColor1?: string;
  jerseyColor2?: string;
  jerseyMode?: JerseyModeEnum;
}> = ({
        image,
        givenName,
        familyName,
        onClick,
        isOpen,
        onClose,
        message,
        children,
        jerseyColor1,
        jerseyColor2,
        jerseyMode
      }) => {
  const hasImage = !!image;
  const buttonClasses = hasImage
    ? "w-16 h-16 overflow-hidden rounded-full cursor-pointer"
    : "w-16 h-16 cursor-pointer";

  return (
    <div className="relative flex flex-col items-center justify-center">
      <div className="relative w-16 h-16">
        <ScaleFade isOpen hoverOnContainer className="w-16 h-16">
          <button type="button" onClick={onClick} className={buttonClasses}>
            <PlayerImage
              image={image}
              width={64}
              className="rounded-full"
              jerseyColor1={jerseyColor1}
              jerseyColor2={jerseyColor2}
              jerseyMode={jerseyMode}
            />
          </button>
        </ScaleFade>
        <div className="absolute inset-0 pointer-events-none">{children}</div>
        {isOpen && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
            <StoreMiniBanner compact noBorder message={message} onClose={onClose} />
          </div>
        )}
      </div>
      <div className="mt-1 flex flex-col items-center justify-center text-center">
        <span className="text-xs font-semibold">{familyName}</span>
        <span className="text-xs">{givenName}</span>
      </div>
    </div>
  );
};

export declare type LineupPositionPlayerField = {
  player: Player | PlayerListElement;
  team: TeamListElement;
  change: boolean;
  card: CardEvent | undefined;
  goal: number;
  position: LineupPosition;
  rating: number | null;
};

const InFieldPlayer: React.FC<{
  p: LineupPositionPlayerField;
  rotated: boolean;
}> = ({ rotated, p }) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const goals = p.goal !== 0 ? (
    <div className="absolute -right-1 -top-1">
      <IconWithBackground icon={faFutbol} className="w-4 h-4 text-black" />
    </div>
  ) : null;
  const H_MARGIN = 0;
  const V_INNER = 3;
  const HALF_HEIGHT = 46;
  const leftPct = p.position.x * (100 - 2 * H_MARGIN) + H_MARGIN;
  const leftFinal = rotated ? 100 - leftPct : leftPct;
  const INNER_MARGIN_PCT = 12;
  const boundedPercent = Math.min(100 - INNER_MARGIN_PCT, Math.max(INNER_MARGIN_PCT, leftFinal));
  const topBase = p.position.y * HALF_HEIGHT + V_INNER;
  const topPct = rotated ? 100 - topBase : topBase;

  return (
    <div
      key={`lineup-pos-${p.player?.id}-${p.position.id}`}
      className="absolute transform -translate-x-1/2 -translate-y-1/2"
      style={{
        left: `${boundedPercent}%`,
        top: `${topPct}%`
      }}
    >
      <PlayerChip
        image={p.player.photo ?? undefined}
        givenName={p.player.givenName}
        familyName={p.player.familyName}
        onClick={() => {
          if (p.player?.id) {
            navigate(pathPlayerView(p.player.id));
          } else {
            setOpen(true);
          }
        }}
        isOpen={open}
        onClose={() => setOpen(false)}
        message={translate("downloadUnlimited", l)}
        jerseyColor1={p.team.jerseyColor1}
        jerseyColor2={p.team.jerseyColor2}
        jerseyMode={p.team.jerseyMode}
      >
        {p.rating !== null && (
          <div className="absolute -left-1 -top-1">
            <PlayerRating rating={p.rating} size="sm" showShadow />
          </div>
        )}

        {p.change && (
          <div className="absolute -right-1 -top-1">
            <FontAwesomeIcon icon={faCircleArrowDown} className="w-5 h-5 text-red" />
          </div>
        )}

        {goals && (
          <div className="absolute -left-1 -bottom-1">
            <IconWithBackground icon={faFutbol} className="w-5 h-5 text-black" />
          </div>
        )}

        {p.card && (
          <div
            className={`absolute -right-1 -bottom-1 rounded-sm ${
              p.card.cardType.key === CardTypeEnum.RED ? "bg-red-card" : "bg-yellow-card"
            } w-3.5 h-5`}
          >
            {p.card.cardType.key === CardTypeEnum.YELLOW_RED && (
              <div className="absolute right-1 -bottom-1 rounded-sm bg-red-card w-3.5 h-5" />
            )}
          </div>
        )}
      </PlayerChip>
    </div>
  );
};

export default InFieldPlayer;
